import React from "react";
import { motion } from "framer-motion";
import { Check, Star, Lightning } from "@phosphor-icons/react";

const packages = [
  {
    name: "Starter",
    price: "$2,500",
    period: "per campaign",
    description: "Perfect for brands testing influencer marketing",
    highlight: false,
    features: [
      "1 Instagram Reel (30-60s)",
      "3 Instagram Stories",
      "Content usage rights (30 days)",
      "Basic analytics report",
      "1 revision round",
    ],
  },
  {
    name: "Growth",
    price: "$5,000",
    period: "per campaign",
    description: "Our most popular package for serious brands",
    highlight: true,
    features: [
      "2 Instagram Reels (30-60s)",
      "5 Instagram Stories with swipe-up",
      "1 YouTube integration",
      "Content usage rights (90 days)",
      "Detailed analytics + ROI report",
      "2 revision rounds",
      "Cross-platform repurpose",
    ],
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "tailored scope",
    description: "Full-scale brand ambassador partnerships",
    highlight: false,
    features: [
      "Multi-platform content creation",
      "Monthly content calendar",
      "Exclusive brand ambassador",
      "Unlimited content usage rights",
      "Weekly performance reporting",
      "Unlimited revisions",
      "Event appearances",
      "Product co-creation",
    ],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#121212] relative"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 text-center"
        >
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-4">
            Services & Packages
          </p>
          <h2
            data-testid="services-heading"
            className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white"
          >
            Investment Tiers
          </h2>
          <p className="text-base text-white/40 mt-4 max-w-lg mx-auto">
            Flexible packages designed for every stage of brand growth
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {packages.map((pkg) => (
            <motion.div
              key={pkg.name}
              variants={itemVariants}
              data-testid={`package-card-${pkg.name.toLowerCase()}`}
              className={`relative border rounded-xl p-8 flex flex-col transition-all duration-300 hover:-translate-y-2 ${
                pkg.highlight
                  ? "bg-[#0A0A0A] border-[#FF3B30]/50 shadow-[0_0_40px_rgba(255,59,48,0.1)]"
                  : "bg-[#1A1A1A] border-white/10 hover:border-white/30"
              }`}
            >
              {pkg.highlight && (
                <div className="absolute -top-px left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#FF3B30] to-transparent" />
              )}

              {pkg.highlight && (
                <div className="flex items-center gap-2 mb-4">
                  <Star size={16} weight="fill" className="text-[#FF3B30]" />
                  <span className="text-xs uppercase tracking-[0.15em] font-bold text-[#FF3B30]">
                    Most Popular
                  </span>
                </div>
              )}

              <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-2">
                {pkg.name}
              </h3>
              <p className="text-sm text-white/40 mb-6">{pkg.description}</p>

              <div className="mb-8">
                <span className="font-['Barlow_Condensed'] text-5xl font-black text-white">
                  {pkg.price}
                </span>
                <span className="text-sm text-white/30 ml-2">/ {pkg.period}</span>
              </div>

              <div className="flex flex-col gap-3 mb-8 flex-grow">
                {pkg.features.map((feature) => (
                  <div key={feature} className="flex items-start gap-3">
                    <Check
                      size={16}
                      weight="bold"
                      className={`mt-0.5 flex-shrink-0 ${
                        pkg.highlight ? "text-[#FF3B30]" : "text-white/30"
                      }`}
                    />
                    <span className="text-sm text-white/60">{feature}</span>
                  </div>
                ))}
              </div>

              <a
                href="#contact"
                data-testid={`package-cta-${pkg.name.toLowerCase()}`}
                className={`w-full text-center py-4 font-bold uppercase tracking-wider text-sm transition-all ${
                  pkg.highlight
                    ? "bg-[#FF3B30] text-white hover:bg-[#FF5C54]"
                    : "bg-transparent text-white border border-white/20 hover:bg-white hover:text-black"
                }`}
              >
                {pkg.price === "Custom" ? "Get a Quote" : "Book This Package"}
              </a>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
